import { BrowserRouter, Navigate, Routes, Route } from 'react-router-dom';

import Layout from '../components/layout/Layout';
import { useAuth } from '../context/AuthContext';
import ActivityPage from '../pages/activity/ActivityPage';
import Dashboard from '../pages/Dashboard';
import CategoriesList from '../pages/categories/CategoriesList';
import ProductDetails from '../pages/products/ProductDetails';
import ProductsList from '../pages/products/ProductsList';
import SettingsPage from '../pages/settings/SettingsPage';
import SuppliersList from '../pages/suppliers/SuppliersList';
import SupplierDetails from '../pages/suppliers/SupplierDetails';
import SupplierEdit from '../pages/suppliers/SupplierEdit';
import SupplierNew from '../pages/suppliers/SupplierNew';
import UsersList from '../pages/users/UsersList';

function withLayout(component) {
  return <Layout>{component}</Layout>;
}

function AuthenticatedRoute({ children }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <section
        style={{
          minHeight: '60vh',
          display: 'grid',
          placeItems: 'center',
          textAlign: 'center',
          padding: 24,
        }}
      >
        <p style={{ margin: 0, color: '#64748b' }}>Checking access...</p>
      </section>
    );
  }

  if (!user) {
    return (
      <section
        style={{
          minHeight: '60vh',
          display: 'grid',
          placeItems: 'center',
          textAlign: 'center',
          padding: 24,
        }}
      >
        <div style={{ maxWidth: 420 }}>
          <h2 style={{ marginBottom: 8 }}>You are not authorized to access this system</h2>
          <p style={{ margin: 0, color: '#64748b' }}>
            Sign in to view user accounts.
          </p>
        </div>
      </section>
    );
  }

  return children;
}

function AdminRoute({ children }) {
  const { user, isAdmin, isLoading } = useAuth();

  if (isLoading) {
    return (
      <section
        style={{
          minHeight: '60vh',
          display: 'grid',
          placeItems: 'center',
          textAlign: 'center',
          padding: 24,
        }}
      >
        <p style={{ margin: 0, color: '#64748b' }}>Checking access...</p>
      </section>
    );
  }

  if (!user || !isAdmin) {
    return (
      <section
        style={{
          minHeight: '60vh',
          display: 'grid',
          placeItems: 'center',
          textAlign: 'center',
          padding: 24,
        }}
      >
        <div style={{ maxWidth: 420 }}>
          <h2 style={{ marginBottom: 8 }}>You are not authorized to access this system</h2>
          <p style={{ margin: 0, color: '#64748b' }}>
            This area is restricted to admin accounts.
          </p>
        </div>
      </section>
    );
  }

  return children;
}

export default function Router() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={withLayout(<Dashboard />)} />
        <Route
          path="/suppliers"
          element={withLayout(<SuppliersList viewMode="active" />)}
        />
        <Route
          path="/suppliers/trash"
          element={withLayout(<SuppliersList viewMode="deleted" />)}
        />
        <Route path="/suppliers/:id" element={withLayout(<SupplierDetails />)} />
        <Route
          path="/products"
          element={withLayout(<ProductsList viewMode="active" />)}
        />
        <Route
          path="/products/trash"
          element={withLayout(<ProductsList viewMode="deleted" />)}
        />
        <Route
          path="/products/:id"
          element={withLayout(<ProductDetails />)}
        />
        <Route
          path="/bundles"
          element={<Navigate to="/products" replace />}
        />
        <Route path="/categories" element={withLayout(<CategoriesList />)} />
        <Route
          path="/users"
          element={withLayout(
            <AdminRoute>
              <UsersList />
            </AdminRoute>
          )}
        />
        <Route
          path="/activity"
          element={withLayout(
            <AuthenticatedRoute>
              <ActivityPage />
            </AuthenticatedRoute>
          )}
        />
        <Route
          path="/settings"
          element={withLayout(
            <AdminRoute>
              <SettingsPage />
            </AdminRoute>
          )}
        />
        <Route
          path="/supplier/new"
          element={withLayout(
            <AdminRoute>
              <SupplierNew />
            </AdminRoute>
          )}
        />
        <Route path="/supplier/:id" element={withLayout(<SupplierDetails />)} />
        <Route
          path="/supplier/:id/edit"
          element={withLayout(
            <AdminRoute>
              <SupplierEdit />
            </AdminRoute>
          )}
        />
      </Routes>
    </BrowserRouter>
  );
}
